import { Injectable } from '@angular/core';
import {
  HttpEventType,
  HttpProgressEvent,
} from '@angular/common/http';
import { Observable, Subject, throwError, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  retry,
  retryWhen,
  switchMap,
  tap,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { VideoCompressionService } from '@shared/service/video-compression.service';
import { ApiService } from '@core/http/api.service';

export interface UploadProgress {
  percent: number;
  loaded: number;
  total: number;
  speed?: string;
  timeRemaining?: string;
  status:
    | 'compressing'
    | 'uploading'
    | 'completed'
    | 'error'
    | 'retrying';
  message?: string;
}

export interface ChunkedUploadOptions {
  chunkSize?: number; // Size in MB
  maxRetries?: number;
  retryDelay?: number; // Delay in ms
  compress?: boolean;
  compressionOptions?: any;
}

@Injectable({
  providedIn: 'root',
})
export class VideoUploadService {
  private uploadProgress$ = new Subject<UploadProgress>();
  private uploadStartTime = 0;
  private lastLoaded = 0;
  private lastTime = 0;

  constructor(
    private http: ApiService,
    private compressionService: VideoCompressionService,
    private translate: TranslateService,
  ) {}

  uploadVideoWithCompression(
    url: string,
    file: File,
    options: ChunkedUploadOptions = {},
  ): Observable<any> {
    const {
      compress = true,
      compressionOptions = {},
      maxRetries = 3,
      retryDelay = 2000,
    } = options;

    return new Observable((observer) => {
      const compressionStep = compress
        ? this.compressVideoWithProgress(file, compressionOptions)
        : Promise.resolve(file);

      compressionStep
        .then((compressedFile) => {
          this.uploadWithRetry(
            url,
            compressedFile,
            maxRetries,
            retryDelay,
          ).subscribe({
            next: (response) => {
              observer.next(response);
            },
            error: (error) => {
              observer.error(error);
            },
            complete: () => {
              observer.complete();
            },
          });
        })
        .catch((error) => {
          console.error(
            'Compression failed, uploading original file:',
            error,
          );
          this.uploadWithRetry(
            url,
            file,
            maxRetries,
            retryDelay,
          ).subscribe({
            next: (response) => {
              observer.next(response);
            },
            error: (err) => {
              observer.error(err);
            },
            complete: () => {
              observer.complete();
            },
          });
        });
    });
  }

  private async compressVideoWithProgress(
    file: File,
    options: any,
  ): Promise<File> {
    this.uploadProgress$.next({
      percent: 0,
      loaded: 0,
      total: file.size,
      status: 'compressing',
      message: `${this.translate.instant(
        'EVIDENCE.COMPRESSION-VIDEO',
      )}...`,
    });

    const fileSizeMB = file.size / (1024 * 1024);
    const recommendedSettings =
      this.compressionService.getRecommendedSettings(fileSizeMB);
    const compressionOptions = { ...recommendedSettings, ...options };

    try {
      const compressedFile = await this.compressionService
        .compressVideo(file, compressionOptions)
        .toPromise();

      if (!compressedFile) {
        throw new Error('Compression returned undefined file');
      }

      const compressionRatio = (
        ((file.size - compressedFile.size) / file.size) *
        100
      ).toFixed(1);

      this.uploadProgress$.next({
        percent: 100,
        loaded: compressedFile.size,
        total: compressedFile.size,
        status: 'compressing',
        message: `${this.translate.instant(
          'EVIDENCE.COMPRESSION-COMPLETED',
        )} ${compressionRatio}%`,
      });

      return compressedFile;
    } catch (error) {
      console.error('Compression failed:', error);
      throw error;
    }
  }

  private uploadWithRetry(
    url: string,
    file: File,
    maxRetries: number,
    retryDelay: number,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    let retryCount = 0;

    return this.http.postWithProgress(url, formData).pipe(
      tap((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.updateProgress(event, file.size);
        } else if (event.type === HttpEventType.Response) {
          this.uploadProgress$.next({
            percent: 100,
            loaded: file.size,
            total: file.size,
            status: 'completed',
            message: this.translate.instant(
              'EVIDENCE.UPLOAD-SUCCESSFULLY',
            ),
          });
        }
      }),
      filter((event) => event.type === HttpEventType.Response),
      map((event: any) => event.body),
      retryWhen((errors) =>
        errors.pipe(
          tap(() => {
            retryCount++;
            if (retryCount <= maxRetries) {
              this.uploadProgress$.next({
                percent: 0,
                loaded: 0,
                total: file.size,
                status: 'retrying',
                message: `${this.translate.instant(
                  'EVIDENCE.CONNECTION-FAILED-RETRYING',
                )} (${retryCount}/${maxRetries})...`,
              });
            }
          }),
          switchMap((error, index) => {
            if (index < maxRetries) {
              return timer(retryDelay * Math.pow(2, index)); // Exponential backoff
            }
            return throwError(error);
          }),
        ),
      ),
      catchError((error) => {
        this.uploadProgress$.next({
          percent: 0,
          loaded: 0,
          total: file.size,
          status: 'error',
          message: this.translate.instant(
            'EVIDENCE.UPLOAD-FAILED-MULTIPLE-ATTEMPT',
          ),
        });
        return throwError(error);
      }),
    );
  }

  uploadVideoInChunks(
    url: string,
    file: File,
    options: ChunkedUploadOptions = {},
  ): Observable<any> {
    const {
      chunkSize = 1, // 1MB chunks by default
      maxRetries = 3,
      retryDelay = 2000,
    } = options;

    const chunkSizeBytes = chunkSize * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSizeBytes);

    return new Observable((observer) => {
      let currentChunk = 0;
      const uploadedChunks: any[] = [];

      const uploadNextChunk = () => {
        if (currentChunk >= totalChunks) {
          this.combineChunks(
            url,
            uploadedChunks,
            file.name,
          ).subscribe({
            next: (response) => {
              observer.next(response);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            },
          });
          return;
        }

        const start = currentChunk * chunkSizeBytes;
        const end = Math.min(start + chunkSizeBytes, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('chunkNumber', currentChunk.toString());
        formData.append('totalChunks', totalChunks.toString());
        formData.append('fileName', file.name);

        this.uploadChunkWithRetry(
          url + 'chunk',
          formData,
          maxRetries,
          retryDelay,
        ).subscribe({
          next: (response) => {
            uploadedChunks.push(response);
            currentChunk++;

            const overallProgress =
              (currentChunk / totalChunks) * 100;
            this.uploadProgress$.next({
              percent: overallProgress,
              loaded: currentChunk * chunkSizeBytes,
              total: file.size,
              status: 'uploading',
              message: `${this.translate.instant(
                'EVIDENCE.UPLOADING-CHUNK',
              )} ${currentChunk}/${totalChunks}`,
            });

            uploadNextChunk();
          },
          error: (error) => {
            observer.error(error);
          },
        });
      };

      uploadNextChunk();
    });
  }

  private uploadChunkWithRetry(
    url: string,
    formData: FormData,
    maxRetries: number,
    retryDelay: number,
  ): Observable<any> {
    return this.http.postAbsolute(url, formData).pipe(
      retry({
        count: maxRetries,
        delay: retryDelay,
      }),
      catchError((error) => {
        console.error('Chunk upload failed:', error);
        return throwError(error);
      }),
    );
  }

  private combineChunks(
    url: string,
    chunks: any[],
    fileName: string,
  ): Observable<any> {
    return this.http.postAbsolute(url + '/combine', {
      chunks: chunks,
      fileName: fileName,
    });
  }

  private updateProgress(
    event: HttpProgressEvent,
    totalSize: number,
  ): void {
    if (!event.loaded) return;

    const currentTime = Date.now();

    if (this.uploadStartTime === 0) {
      this.uploadStartTime = currentTime;
      this.lastTime = currentTime;
      this.lastLoaded = 0;
    }

    const timeDiff = (currentTime - this.lastTime) / 1000; // seconds
    const loadedDiff = event.loaded - this.lastLoaded;

    let speed = '';
    let timeRemaining = '';

    if (timeDiff > 0) {
      const bytesPerSecond = loadedDiff / timeDiff;
      speed = this.formatSpeed(bytesPerSecond);

      const remainingBytes = totalSize - event.loaded;
      const remainingSeconds = remainingBytes / bytesPerSecond;
      timeRemaining = this.formatTime(remainingSeconds);
    }

    this.lastTime = currentTime;
    this.lastLoaded = event.loaded;

    const percent = event.total
      ? Math.round((100 * event.loaded) / event.total)
      : 0;

    this.uploadProgress$.next({
      percent,
      loaded: event.loaded,
      total: event.total || totalSize,
      speed,
      timeRemaining,
      status: 'uploading',
      message: `${this.translate.instant(
        'EVIDENCE.UPLOADING',
      )}... ${percent}%`,
    });
  }

  private formatSpeed(bytesPerSecond: number): string {
    if (bytesPerSecond < 1024) {
      return `${bytesPerSecond.toFixed(0)} B/s`;
    } else if (bytesPerSecond < 1024 * 1024) {
      return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
    } else {
      return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
    }
  }

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) {
      return `${this.translate.instant('EVIDENCE.CALCULATING')}...`;
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  getUploadProgress(): Observable<UploadProgress> {
    return this.uploadProgress$.asObservable();
  }
}
